import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, X, Send, Loader2, ChevronLeft, Briefcase, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ChatDialog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  // Fetch all conversations (properties user has messaging access to)
  const fetchConversations = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/api/messages/conversations`);
      setConversations(res.data.conversations || []);
      setUnreadTotal(res.data.total_unread || 0);
    } catch (e) {
      logger.error('Failed to fetch conversations:', e);
    }
  }, [user]);

  // Fetch messages for active conversation
  const fetchMessages = useCallback(async () => {
    if (!activeConvo) return;
    try {
      const res = await axios.get(`${API_URL}/api/messages/${activeConvo.property_id}`);
      setMessages(res.data.messages || []);
    } catch (e) {
      logger.error('Failed to fetch messages:', e);
    } finally {
      setLoading(false);
    }
  }, [activeConvo]);

  useEffect(() => {
    if (open && !activeConvo) fetchConversations();
  }, [open, activeConvo, fetchConversations]);

  useEffect(() => {
    if (activeConvo) {
      setLoading(true);
      fetchMessages();
      pollRef.current = setInterval(fetchMessages, 8000);
      return () => clearInterval(pollRef.current);
    }
  }, [activeConvo, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Periodically fetch conversations to update unread count (only when dialog is closed)
  useEffect(() => {
    if (!user) return;
    fetchConversations();
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, [user, fetchConversations]);

  // Auto-open when notification deep-links to /property/{id}?tab=messages
  useEffect(() => {
    if (!user) return;
    const tab = searchParams.get('tab');
    if (tab !== 'messages') return;
    const m = location.pathname.match(/^\/property\/([^/]+)/);
    if (!m) return;
    const propertyId = m[1];
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/messages/conversations`);
        const convos = res.data.conversations || [];
        const found = convos.find(c => c.property_id === propertyId);
        if (found) {
          setConversations(convos);
          setActiveConvo(found);
          setOpen(true);
        } else {
          // User has no active conversation for this property yet
          toast.info('Messaging access not approved yet for this property.');
        }
      } catch (e) {
        logger.error('Auto-open chat failed:', e);
      }
    })();
  }, [user, location.pathname, searchParams]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeConvo) return;
    setSendLoading(true);
    try {
      await axios.post(`${API_URL}/api/messages/${activeConvo.property_id}`, { text: text.trim() });
      setText('');
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setSendLoading(false);
    }
  };

  const formatTime = (isoStr) => {
    try {
      const d = new Date(isoStr);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Now';
      if (diffMins < 60) return `${diffMins}m`;
      const diffHrs = Math.floor(diffMins / 60);
      if (diffHrs < 24) return `${diffHrs}h`;
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  if (!user) return null;

  return (
    <>
      {/* Floating chat button — bottom LEFT, teal theme to avoid conflict with AI bot on right */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-teal-600 text-white rounded-full shadow-lg hover:bg-teal-700 transition-all flex items-center justify-center group"
          data-testid="chat-dialog-trigger"
        >
          <MessageCircle className="h-6 w-6" />
          {unreadTotal > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unreadTotal > 9 ? '9+' : unreadTotal}
            </span>
          )}
        </button>
      )}

      {/* Chat dialog — anchored to bottom LEFT */}
      {open && (
        <div className="fixed bottom-6 left-6 z-50 w-[380px] max-h-[520px] bg-white border border-slate-200 shadow-2xl rounded-lg flex flex-col overflow-hidden" data-testid="chat-dialog">
          {/* Header */}
          <div className="bg-teal-700 text-white px-4 py-3 flex items-center justify-between flex-shrink-0 rounded-t-lg">
            {activeConvo ? (
              <>
                <button onClick={() => { setActiveConvo(null); setMessages([]); fetchConversations(); }} className="flex items-center gap-2 text-sm hover:text-teal-200 transition-colors" data-testid="chat-back-btn">
                  <ChevronLeft className="h-4 w-4" />
                  <span className="font-medium truncate max-w-[220px]">{activeConvo.property_title}</span>
                </button>
                <div className="flex items-center gap-1">
                  <button onClick={() => navigate(`/property/${activeConvo.property_id}`)} className="text-white/50 hover:text-white text-xs" data-testid="chat-view-property">View</button>
                  <button onClick={() => setOpen(false)} className="ml-2 text-white/50 hover:text-white" data-testid="chat-close"><X className="h-4 w-4" /></button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-teal-200" />
                  <span className="font-semibold text-sm tracking-wide">Messages</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white" data-testid="chat-minimize"><Minimize2 className="h-4 w-4" /></button>
                </div>
              </>
            )}
          </div>

          {/* Conversation list */}
          {!activeConvo && (
            <div className="flex-1 overflow-y-auto" data-testid="conversation-list">
              {conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400 text-sm px-6 text-center">
                  <MessageCircle className="h-10 w-10 text-slate-200 mb-3" />
                  <p className="font-medium">No conversations yet</p>
                  <p className="text-xs mt-1 text-slate-300">Start by requesting contact on a property you're interested in</p>
                </div>
              ) : (
                conversations.map((convo) => (
                  <button
                    key={convo.property_id}
                    onClick={() => setActiveConvo(convo)}
                    className="w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors flex items-start gap-3"
                    data-testid={`convo-${convo.property_id}`}
                  >
                    <div className="w-10 h-10 bg-teal-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-teal-700 font-semibold text-sm">{(convo.property_title || 'P').charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-[#1A1A1A] truncate">{convo.property_title || 'Property'}</p>
                        {convo.last_message_at && (
                          <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">{formatTime(convo.last_message_at)}</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{convo.last_message || 'No messages yet'}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{convo.counterpart_name || ''}</p>
                    </div>
                    {convo.unread_count > 0 && (
                      <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        {convo.unread_count}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}

          {/* Active conversation messages */}
          {activeConvo && (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#F8FAFC] min-h-0" style={{ maxHeight: '340px' }} data-testid="chat-messages">
                {loading ? (
                  <div className="flex items-center justify-center h-40"><Loader2 className="h-5 w-5 text-teal-600 animate-spin" /></div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center h-40 text-slate-400 text-xs">No messages yet</div>
                ) : (
                  messages.map((msg) => {
                    const isMe = msg.sender_id === user?.id;
                    return (
                      <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] px-3 py-2 ${isMe ? 'bg-teal-600 text-white' : 'bg-white border border-slate-200 text-[#1A1A1A]'}`}>
                          {!isMe && <p className="text-[10px] font-semibold text-teal-700 mb-0.5">{msg.sender_name}</p>}
                          {msg.representative_name && (
                            <p className={`text-[10px] mb-0.5 flex items-center gap-1 ${isMe ? 'text-white/60' : 'text-slate-400'}`}>
                              <Briefcase className="h-2.5 w-2.5" /> via {msg.representative_name}
                            </p>
                          )}
                          <p className="text-sm leading-relaxed">{msg.text}</p>
                          <p className={`text-[10px] mt-0.5 ${isMe ? 'text-white/50' : 'text-slate-300'}`}>{formatTime(msg.created_at)}</p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Send input */}
              <form onSubmit={handleSend} className="flex items-center gap-2 p-3 border-t border-slate-200 bg-white flex-shrink-0">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 h-10 px-3 text-sm border border-slate-200 bg-[#F8FAFC] text-[#1A1A1A] outline-none focus:border-teal-500 transition-colors"
                  data-testid="chat-input"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
                />
                <button
                  type="submit"
                  disabled={sendLoading || !text.trim()}
                  className="w-10 h-10 bg-teal-600 text-white flex items-center justify-center hover:bg-teal-700 disabled:opacity-40 transition-colors flex-shrink-0"
                  data-testid="chat-send-btn"
                >
                  {sendLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatDialog;
